/* @flow */
import React from 'react';
import cx from 'classnames';

import Checkbox from 'components/Checkbox.jsx';
import Divider from 'components/Divider.jsx';
import List from 'components/List.jsx';

import './styles/CheckboxList.scss';

/**
 * Renders a list of checkboxes.
 *
 * ## CheckboxList can be used as a uncontrolled element
 *
 * ```example
 *  <CheckboxList
 *    options={{
 *      'apples': 'Apples',
 *      'bananas': 'Bananas',
 *      'oranges': 'Oranges',
 *      'strawberries': 'Strawberries',
 *    }}
 *    defaultChecked={['bananas']}
 *    onChange={checked => window.alert(checked)}
 *  />
 * ```
 */

class CheckboxList extends React.Component {
  static propTypes = {
    /** Field name to group the checkbox elements */
    name: React.PropTypes.string,
    /**
     * Key-value pairs where the key is the value of the option. When using an array, the key and
     * value are the same.
     */
    options: React.PropTypes.oneOfType([
      React.PropTypes.instanceOf(Map),
      React.PropTypes.object,
      React.PropTypes.array,
    ]).isRequired,
    /** Array of checked values */
    checked: React.PropTypes.array,
    /** Whether the checkboxes are all disabled or not */
    disabled: React.PropTypes.array,
    /** Initial array of checked values for uncontrolled input */
    defaultChecked: React.PropTypes.array,
    /** Callback called when the checked state changes. Called with an array of checked values. */
    onChange: React.PropTypes.func,
    /** Event name when clicking on the checkboxes */
    eventElementName: React.PropTypes.string,
    /** Whether to float the checkbox on the left or the right */
    checkboxAlignment: React.PropTypes.oneOf(['default', 'left', 'right']),
    /** Whether to show row dividers */
    showRowDividers: React.PropTypes.bool,
    /** Whether to show as a vertical or horizontal list */
    direction: React.PropTypes.oneOf(['vertical', 'horizontal']),
  };

  static defaultProps = {
    checkboxAlignment: 'default',
    showRowDividers: false,
    direction: 'vertical',
  };

  state = {
    initialChecked: this.props.defaultChecked || [],
    checked: Array.from(this.props.checked || this.props.defaultChecked || []),
  };

  componentWillReceiveProps(nextProps: Object) {
    if (this.props.checked !== nextProps.checked) {
      this.setState({ checked: Array.from(nextProps.checked) });
    }
  }

  getValue = () => {
    return this.state.checked;
  };

  handleChange = (value: string|number) => {
    const { checked } = this.state;
    const index = checked.indexOf(value);
    if (index >= 0) {
      checked.splice(index, 1);
    } else {
      checked.push(value);
    }
    this.setState({ checked });

    if (this.props.onChange) {
      this.props.onChange(checked);
    }
  };

  renderCheckbox = (value: string|number, label: mixed, lastItem: boolean) => {
    const { checked, disabled, checkboxAlignment, showRowDividers } = this.props;
    // By default, the label is given as a child of the Checkbox
    // which renders it to the right of the checkbox input
    // If we want to float the checkbox input, we have to move the
    // label outside of the Checkbox component
    const labelAsChildOfCheckbox = checkboxAlignment === 'default';
    return (
      <li
        className={cx({
          'CheckboxList-item-left': checkboxAlignment === 'left',
          'CheckboxList-item-right': checkboxAlignment === 'right',
          'CheckboxList-item-horizontal': this.props.direction === 'horizontal',
        }, 'CheckboxList-item')} key={value}>
        <Checkbox
          name={this.props.name}
          value={value}
          disabled={disabled && disabled.includes(value)}
          checked={checked && checked.includes(value)}
          defaultChecked={checked ? null : this.state.initialChecked.includes(value)}
          eventElementName={this.props.eventElementName}
          onChange={() => this.handleChange(value)}
          checkboxAlignment={this.props.checkboxAlignment}>
          {labelAsChildOfCheckbox && label}
        </Checkbox>
        {!labelAsChildOfCheckbox && label}
        {showRowDividers && !lastItem && <Divider size="small" />}
      </li>
    );
  };

  render() {
    let items = [];
    const { options } = this.props;
    if (options instanceof Map) {
      let elementsRemaining = options.size;
      options.forEach((label, value) => {
        items.push(this.renderCheckbox(value, label, --elementsRemaining === 0));
      });
    } else if (Array.isArray(options)) {
      items = options.map((label, index) => {
        return this.renderCheckbox(index, label, index === options.length - 1);
      });
    } else {
      const keys = Object.keys(options);
      items = options.map((value, index) => {
        return this.renderCheckbox(value, options[value], index === keys.length - 1);
      });
    }

    const {onChange, ...listProps} = this.props; // eslint-disable-line

    return (
      <List
        {...listProps}
        className={cx('CheckboxList', this.props.className)}>
        {items}
      </List>
    );
  }
}

export default CheckboxList;



// WEBPACK FOOTER //
// ./src/components/CheckboxList.jsx